/**
 * SearchIndexRepository
 * 
 * Generic repository for managing search indexes in Firestore.
 * Handles CRUD operations, batch operations, and statistics.
 * 
 * Not property-specific - works with any SearchIndex configuration.
 * 
 * Usage:
 * const repo = new SearchIndexRepository(db, PropertySearchIndex);
 * await repo.syncOne('property123', propertyData);
 * const results = await repo.search('birmingham', {deliveryStatus: 'delivered'});
 */

import { SearchIndex } from './SearchIndex';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  documentId,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  Timestamp
} from 'firebase/firestore';

// How much further than the result cap to scan when other words will filter
// the query term's matches down. Index entries are ~450 bytes.
const NARROWING_SCAN_FACTOR = 3;

export class SearchIndexRepository {
  /**
   * @param {Firestore} db - Firestore instance
   * @param {SearchIndex} searchIndex - SearchIndex configuration
   */
  constructor(db, searchIndex) {
    this.db = db;
    this.searchIndex = searchIndex;
    this.indexCollectionRef = collection(db, searchIndex.indexCollection);
  }

  /**
   * Sync a single document to the search index
   * @param {string} sourceId - Source document ID
   * @param {Object} sourceData - Source document data
   * @returns {Promise<Object>} Search index document
   */
  async syncOne(sourceId, sourceData) {
    try {
      const searchDoc = this.searchIndex.toSearchDoc(sourceId, sourceData);
      
      // Validate before writing
      const validation = this.searchIndex.validate(searchDoc);
      if (!validation.valid) {
        console.error(`[SearchIndexRepository] Validation failed for ${sourceId}:`, validation.errors);
        throw new Error(`Invalid search document: ${validation.errors.join(', ')}`);
      }

      const indexDocRef = doc(this.indexCollectionRef, sourceId);
      await setDoc(indexDocRef, {
        ...searchDoc,
        updatedAt: Timestamp.now()
      });

      return searchDoc;
    } catch (error) {
      console.error(`[SearchIndexRepository] Error syncing ${sourceId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a document from the search index
   * @param {string} sourceId - Source document ID
   */
  async deleteOne(sourceId) {
    try {
      const indexDocRef = doc(this.indexCollectionRef, sourceId);
      await deleteDoc(indexDocRef);
    } catch (error) {
      console.error(`[SearchIndexRepository] Error deleting ${sourceId}:`, error);
      throw error;
    }
  }

  /**
   * Rebuild entire index from source collection
   * @param {Firestore} db - Firestore instance (for source collection access)
   * @param {Function} progressCallback - Optional callback(processed, total)
   * @returns {Promise<Object>} Rebuild statistics
   */
  async rebuildIndex(db, progressCallback) {
    
    const startTime = Date.now();
    const stats = {
      processed: 0,
      created: 0,
      errors: 0,
      errorDetails: []
    };

    try {
      // Fetch all source documents
      const sourceCollectionRef = collection(db, this.searchIndex.sourceCollection);
      const sourceSnapshot = await getDocs(sourceCollectionRef);
      
      const totalDocs = sourceSnapshot.size;

      // Process in batches of 500 (Firestore batch limit)
      const BATCH_SIZE = 500;
      let currentBatch = writeBatch(db);
      let batchCount = 0;

      for (const sourceDoc of sourceSnapshot.docs) {
        try {
          const sourceId = sourceDoc.id;
          const sourceData = sourceDoc.data();

          // Generate search document
          const searchDoc = this.searchIndex.toSearchDoc(sourceId, sourceData);
          
          // Validate
          const validation = this.searchIndex.validate(searchDoc);
          if (!validation.valid) {
            stats.errors++;
            stats.errorDetails.push({
              id: sourceId,
              errors: validation.errors
            });
            continue;
          }

          // Add to batch
          const indexDocRef = doc(this.indexCollectionRef, sourceId);
          currentBatch.set(indexDocRef, {
            ...searchDoc,
            updatedAt: Timestamp.now()
          });
          
          batchCount++;
          stats.processed++;

          // Commit batch when full
          if (batchCount >= BATCH_SIZE) {
            await currentBatch.commit();
            stats.created += batchCount;
            
            // Start new batch
            currentBatch = writeBatch(db);
            batchCount = 0;

            // Progress callback
            if (progressCallback) {
              progressCallback(stats.processed, totalDocs);
            }
          }
        } catch (error) {
          console.error(`[SearchIndexRepository] Error processing ${sourceDoc.id}:`, error);
          stats.errors++;
          stats.errorDetails.push({
            id: sourceDoc.id,
            error: error.message
          });
        }
      }

      // Commit remaining batch
      if (batchCount > 0) {
        await currentBatch.commit();
        stats.created += batchCount;
      }

      const duration = Date.now() - startTime;
      stats.durationMs = duration;
      stats.docsPerSecond = Math.round((stats.created / duration) * 1000);

      return stats;
    } catch (error) {
      console.error(`[SearchIndexRepository] Rebuild failed:`, error);
      throw error;
    }
  }

  /**
   * Search the index.
   *
   * Firestore allows ONE array-contains per query, so one term is queried and
   * the rest are checked on what comes back. Which term matters enormously:
   * the query runs on the RAREST one (a count per term, one read per thousand
   * matches), so "12 Broadheath Drive" fetches the handful of Broadheath
   * entries rather than the first thousand containing "drive". See planSearch
   * for how a query becomes terms, and for the bug this replaced.
   *
   * The returned array carries a non-enumerable `truncated` flag: true when
   * the queried term had more matches than `maxResults`, so some real matches
   * were not fetched and the caller should say so.
   *
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Optional filters {field: value} or {field: [values]}
   * @param {number} maxResults - Maximum index entries to fetch
   * @returns {Promise<Object[]>} Matching source documents
   */
  async search(searchTerm, filters = {}, maxResults = 100) {
    const withMeta = (arr, truncated) =>
      Object.defineProperty(arr, 'truncated', { value: truncated, enumerable: false });
    try {
      const plan = this.searchIndex.planSearch(searchTerm);
      if (!plan.indexTerms.length && !plan.concat) return withMeta([], false);

      const indexRef = collection(this.db, this.searchIndex.indexCollection);
      const filterConstraints = [];
      Object.entries(filters).forEach(([field, value]) => {
        if (value === null || value === undefined || value === 'all') return;
        if (Array.isArray(value)) {
          if (value.length) filterConstraints.push(where(field, 'in', value.slice(0, 10)));
        } else {
          filterConstraints.push(where(field, '==', value));
        }
      });
      const termQuery = (t, ...more) =>
        query(indexRef, where('searchTokens', 'array-contains', t), ...filterConstraints, ...more);

      // Counts decide the query term and whether the run-together postcode
      // candidate is real. A single plain term needs no count — it IS the query.
      const candidates = [...plan.indexTerms, ...(plan.concat ? [plan.concat] : [])];
      const counts = {};
      if (candidates.length > 1) {
        const got = await Promise.all(
          candidates.map((t) => getCountFromServer(termQuery(t)).then((r) => r.data().count))
        );
        candidates.forEach((t, i) => { counts[t] = got[i]; });
      }
      const terms = [...plan.indexTerms];
      if (plan.concat && counts[plan.concat] > 0) terms.push(plan.concat);
      if (!terms.length || terms.some((t) => counts[t] === 0)) return withMeta([], false);

      const primary = SearchIndex.choosePrimary(terms, counts);
      // When other words will narrow what comes back, scan past the result
      // cap: "PARK LA" can only query "park" (1,248 entries), and stopping at
      // the cap dropped matches before "la" had a chance to narrow them.
      const narrowing = terms.length > 1 || plan.shortWords.length > 0;
      const scanCap = narrowing ? maxResults * NARROWING_SCAN_FACTOR : maxResults;
      const snapshot = await getDocs(termQuery(primary, limit(scanCap)));
      let truncated = counts[primary] != null
        ? counts[primary] > scanCap
        : snapshot.size >= scanCap;

      let ids = snapshot.docs
        .filter((d) => {
          const tokens = d.data().searchTokens || [];
          return (
            terms.every((t) => tokens.includes(t)) &&
            SearchIndex.tokensMatchShortWords(tokens, plan.shortWords)
          );
        })
        .map((d) => d.id);
      if (ids.length > maxResults) {
        ids = ids.slice(0, maxResults);
        truncated = true;
      }
      if (!ids.length) return withMeta([], truncated);

      // Fetch the documents themselves — the index holds tokens, not data.
      // 30 is Firestore's cap for an 'in' filter (it was 10 when this was written).
      const BATCH_SIZE = 30;
      const batches = [];
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        batches.push(getDocs(query(
          collection(this.db, this.searchIndex.sourceCollection),
          where(documentId(), 'in', ids.slice(i, i + BATCH_SIZE))
        )));
      }
      const snaps = await Promise.all(batches);
      const docs = snaps
        .flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        .filter((d) =>
          SearchIndex.matchesShortWords(d, plan.shortWords, this.searchIndex.searchableFields)
        );
      return withMeta(docs, truncated);
    } catch (error) {
      console.error(`[SearchIndexRepository] Search error:`, error);
      throw error;
    }
  }

  /**
   * Get index statistics
   * @returns {Promise<Object>} Index statistics
   */
  async getStats() {
    try {
      const snapshot = await getDocs(this.indexCollectionRef);
      const docs = snapshot.docs.map(doc => doc.data());
      
      const stats = this.searchIndex.constructor.calculateStats(docs);
      stats.indexCollection = this.searchIndex.indexCollection;
      stats.sourceCollection = this.searchIndex.sourceCollection;

      return stats;
    } catch (error) {
      console.error(`[SearchIndexRepository] Error getting stats:`, error);
      throw error;
    }
  }

  /**
   * Check if index exists and is healthy
   * @returns {Promise<Object>} Health check result
   */
  async healthCheck() {
    try {
      // ⚠️ A COUNT, not getStats(). "Healthy" only means "not empty", and
      // getStats() downloads every index document into the browser to find
      // that out — 48,691 of them in Sep 2026, on every Settings page open.
      // A server-side count costs one read per thousand documents.
      const snap = await getCountFromServer(this.indexCollectionRef);
      const totalDocuments = snap.data().count;

      return {
        healthy: totalDocuments > 0,
        message: totalDocuments > 0
          ? `Index contains ${totalDocuments} documents`
          : 'Index is empty',
        stats: { totalDocuments }
      };
    } catch (error) {
      return {
        healthy: false,
        message: error.message,
        error
      };
    }
  }

  /**
   * Get a single index document
   * @param {string} sourceId - Source document ID
   * @returns {Promise<Object|null>} Index document or null
   */
  async getOne(sourceId) {
    try {
      const indexDocRef = doc(this.indexCollectionRef, sourceId);
      const docSnap = await getDoc(indexDocRef);
      
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    } catch (error) {
      console.error(`[SearchIndexRepository] Error getting ${sourceId}:`, error);
      throw error;
    }
  }
}

export default SearchIndexRepository;
